import React, { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  // Explicitly declare props to satisfy strict TypeScript checks
  public declare props: Readonly<Props>;

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-950 text-slate-200 p-8 text-center">
          <div className="bg-red-900/20 p-6 rounded-full mb-6 ring-1 ring-red-500/50">
            <AlertTriangle className="w-16 h-16 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">System Error</h1>
          <p className="text-slate-400 max-w-md mb-8">
            The application encountered an unexpected error. Detailed logs have been captured.
          </p>
          <div className="bg-black/30 p-4 rounded-lg font-mono text-xs text-red-400 mb-8 max-w-lg overflow-auto border border-red-900/30 text-left w-full max-h-48">
            {this.state.error?.toString()}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-medium transition-all border border-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
            Reload Application
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;